import { useMemo, useState } from "react";

import { ArrowUpRight, Building2, CircleCheckBig, Link as LinkIcon, Star, Users } from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useCan } from "@/lib/auth/useCan";

import { useSelectNote, type Note, type Workspace } from "../api";
import {
  BOARD_META,
  COMPANY_WIDE_DEPT_THRESHOLD,
  impactCategoryMeta,
  type Board,
} from "../constants";
import { PromoteObjectiveDialog } from "./PromoteObjectiveDialog";

/**
 * The exercise's bottom line: initiatives ranked from the revealed scores
 * (impact first, then least effort), split into company-wide plays versus
 * department-specific ones by how many departments they touch. Objectives are
 * created from here.
 */
export function SummaryTab({
  workspace,
  onNavigateBoard,
}: {
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
}) {
  const initiatives = useMemo(
    () =>
      workspace.notes.filter((n) => n.board === "impact_effort" && n.status === "posted"),
    [workspace.notes],
  );

  const scored = initiatives
    .filter((n) => n.vote_summary.revealed && n.vote_summary.avg_impact !== null)
    .sort(
      (a, b) =>
        b.vote_summary.avg_impact! - a.vote_summary.avg_impact! ||
        a.vote_summary.avg_effort! - b.vote_summary.avg_effort! ||
        Number(b.selected) - Number(a.selected),
    );
  const unscored = initiatives.filter(
    (n) => !n.vote_summary.revealed || n.vote_summary.avg_impact === null,
  );

  const companyWide = scored.filter(
    (n) => n.departments.length >= COMPANY_WIDE_DEPT_THRESHOLD,
  );
  const departmental = scored.filter(
    (n) => n.departments.length > 0 && n.departments.length < COMPANY_WIDE_DEPT_THRESHOLD,
  );
  const untagged = scored.filter((n) => n.departments.length === 0);

  if (initiatives.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        Nothing on the grid yet — promote SWOT themes or add initiatives, score
        them, and the ranked takeaways land here.
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Ranked by the room&apos;s scores — highest impact first, least effort breaking
        ties. Company-wide means {COMPANY_WIDE_DEPT_THRESHOLD}+ departments tagged.
      </p>

      <RankedSection
        title="Company-wide"
        icon={Building2}
        notes={companyWide}
        workspace={workspace}
        onNavigateBoard={onNavigateBoard}
        emptyHint="No initiative touches enough departments to be company-wide yet."
      />
      <RankedSection
        title="Department-specific"
        icon={Users}
        notes={departmental}
        workspace={workspace}
        onNavigateBoard={onNavigateBoard}
        emptyHint="Nothing scoped to specific departments yet."
      />
      {untagged.length > 0 ? (
        <RankedSection
          title="No departments tagged yet"
          icon={Users}
          notes={untagged}
          workspace={workspace}
          onNavigateBoard={onNavigateBoard}
        />
      ) : null}
      {unscored.length > 0 ? (
        <p className="text-xs text-muted-foreground">
          {unscored.length} initiative{unscored.length === 1 ? "" : "s"} not scored/revealed
          yet: {unscored.map((n) => n.title).join(", ")}
        </p>
      ) : null}
    </div>
  );
}

function RankedSection({
  title,
  icon: Icon,
  notes,
  workspace,
  onNavigateBoard,
  emptyHint,
}: {
  title: string;
  icon: typeof Building2;
  notes: Note[];
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
  emptyHint?: string;
}) {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        <Icon className="h-4 w-4" />
        {title}
        <span className="font-normal text-muted-foreground">({notes.length})</span>
      </h2>
      {notes.length === 0 ? (
        emptyHint ? <p className="text-xs text-muted-foreground">{emptyHint}</p> : null
      ) : (
        <div className="space-y-2">
          {notes.map((n, i) => (
            <InitiativeRow
              key={n.id}
              rank={i + 1}
              note={n}
              workspace={workspace}
              onNavigateBoard={onNavigateBoard}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function InitiativeRow({
  rank,
  note,
  workspace,
  onNavigateBoard,
}: {
  rank: number;
  note: Note;
  workspace: Workspace;
  onNavigateBoard: (board: Board) => void;
}) {
  const isAdmin = useCan("planning.admin");
  const select = useSelectNote(workspace.session.id);
  const [promoteOpen, setPromoteOpen] = useState(false);

  const category = impactCategoryMeta(note.impact_category);
  const sourceLane = note.source_category_id
    ? workspace.categories.find((c) => c.id === note.source_category_id)
    : undefined;
  const vs = note.vote_summary;

  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border bg-card px-3 py-2",
        note.selected && "border-warning/60",
      )}
    >
      <span className="w-6 text-right font-mono text-sm text-muted-foreground">{rank}</span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          {note.selected ? (
            <Star className="h-3.5 w-3.5 shrink-0 fill-warning text-warning" />
          ) : null}
          <span className="text-sm font-medium">{note.title}</span>
          {category ? (
            <Badge variant="outline" className="text-[10px]">
              {category.label}
            </Badge>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          <span>
            Impact {vs.avg_impact} · Effort {vs.avg_effort} · {vs.count} vote
            {vs.count === 1 ? "" : "s"}
          </span>
          {note.departments.length ? <span>{note.departments.join(", ")}</span> : null}
          {sourceLane ? (
            <button
              type="button"
              className="inline-flex items-center gap-1 text-brand hover:underline"
              onClick={() => onNavigateBoard(sourceLane.board)}
            >
              <LinkIcon className="h-3 w-3" />
              from {BOARD_META[sourceLane.board].label} · {sourceLane.name}
            </button>
          ) : null}
        </div>
      </div>
      {isAdmin ? (
        <div className="flex shrink-0 items-center gap-1.5">
          <Button
            variant={note.selected ? "default" : "outline"}
            size="sm"
            className="h-7 text-xs"
            onClick={() =>
              select.mutate(
                { id: note.id, selected: !note.selected },
                { onError: (err) => toast.error(err.message) },
              )
            }
          >
            <Star className={cn("mr-1 h-3 w-3", note.selected && "fill-warning text-warning")} />
            {note.selected ? "Selected" : "Select"}
          </Button>
          {note.promoted_objective_id ? (
            <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
              <CircleCheckBig className="h-3.5 w-3.5" /> Objective
            </span>
          ) : (
            <Button size="sm" className="h-7 text-xs" onClick={() => setPromoteOpen(true)}>
              <ArrowUpRight className="mr-1 h-3 w-3" /> Objective
            </Button>
          )}
        </div>
      ) : note.promoted_objective_id ? (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-success">
          <CircleCheckBig className="h-3.5 w-3.5" /> Objective
        </span>
      ) : null}
      <PromoteObjectiveDialog
        note={note}
        workspace={workspace}
        open={promoteOpen}
        onOpenChange={setPromoteOpen}
      />
    </div>
  );
}
